import { useEffect, useMemo, useRef, useState } from 'react';
import { useImageBitmaps } from '@/hooks/useImageBitmaps';
import { useObjectUrl } from '@/hooks/useObjectUrl';
import { drawFrame } from '@/lib/render/drawFrame';
import { resolveWatermarkPosition } from '@/lib/render/geometry';
import { getTimelineCaptionCues } from '@/lib/captionTiming';
import { computeTotalDuration } from '@/lib/timeline';
import { withEffectiveSettings } from '@/lib/effectiveSettings';
import { computeMusicVolumeAtTime, computeVoiceoverWindow } from '@/lib/volume';
import type { PreviewPlayback } from './usePreviewPlayback';
import type { ReelProject, SharedDefaults } from '@/types';

interface PreviewPlayerProps {
  project: ReelProject;
  shared: SharedDefaults;
  playback: PreviewPlayback;
}

const PREVIEW_WIDTH = 360;
const PREVIEW_HEIGHT = 640;

export function PreviewPlayer({ project, shared, playback }: PreviewPlayerProps) {
  const effectiveProject = useMemo(() => withEffectiveSettings(project, shared), [project, shared]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const voiceoverAudioRef = useRef<HTMLAudioElement>(null);
  const musicAudioRef = useRef<HTMLAudioElement>(null);
  const audioGraphRef = useRef<{ ctx: AudioContext; voiceGain: GainNode; musicGain: GainNode } | null>(null);

  const mediaIds = useMemo(() => Array.from(new Set(effectiveProject.images.map((i) => i.mediaId))), [effectiveProject.images]);
  const imageBitmaps = useImageBitmaps(mediaIds);
  const captionCues = useMemo(() => getTimelineCaptionCues(effectiveProject), [effectiveProject]);

  const voiceoverUrl = useObjectUrl(effectiveProject.voiceover.generatedMediaId);
  const musicUrl = useObjectUrl(effectiveProject.music.enabled ? effectiveProject.music.mediaId : null);
  const [watermarkBitmap, setWatermarkBitmap] = useState<ImageBitmap | null>(null);
  const [dragTarget, setDragTarget] = useState<'caption' | 'watermark' | null>(null);

  const customWatermarkUrl = useObjectUrl(
    effectiveProject.watermark.enabled && effectiveProject.watermark.source === 'custom' ? effectiveProject.watermark.mediaId : null
  );

  // Load the watermark bitmap (default asset or custom upload).
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!effectiveProject.watermark.enabled) {
        setWatermarkBitmap(null);
        return;
      }
      const src = effectiveProject.watermark.source === 'custom' ? customWatermarkUrl : '/assets/watermark-default.svg';
      if (!src) {
        setWatermarkBitmap(null);
        return;
      }
      const response = await fetch(src);
      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);
      if (!cancelled) setWatermarkBitmap(bitmap);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [effectiveProject.watermark.enabled, effectiveProject.watermark.source, customWatermarkUrl]);

  // Draw the current frame whenever time or any dependency changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    drawFrame({
      ctx,
      canvasWidth: PREVIEW_WIDTH,
      canvasHeight: PREVIEW_HEIGHT,
      time: playback.currentTime,
      project: effectiveProject,
      imageBitmaps,
      watermarkBitmap,
      captionCues,
    });
  }, [playback.currentTime, effectiveProject, imageBitmaps, watermarkBitmap, captionCues]);

  // Set up the Web Audio graph once (lets us apply gain > 100% and precise ducking).
  useEffect(() => {
    if (audioGraphRef.current || !voiceoverAudioRef.current || !musicAudioRef.current) return;
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    const voiceGain = ctx.createGain();
    const musicGain = ctx.createGain();
    const voiceSource = ctx.createMediaElementSource(voiceoverAudioRef.current);
    const musicSource = ctx.createMediaElementSource(musicAudioRef.current);
    voiceSource.connect(voiceGain).connect(ctx.destination);
    musicSource.connect(musicGain).connect(ctx.destination);
    audioGraphRef.current = { ctx, voiceGain, musicGain };
  }, []);

  // Browsers only allow AudioContext.resume() to actually take effect when it runs as part of
  // the same user-gesture event dispatch that unlocked it (e.g. a click). Once an <audio> element
  // is routed into a Web Audio graph (above), it stops sending sound directly to the speakers —
  // all its output goes through the (possibly still-suspended) context — so relying on a React
  // effect to resume() *after* the click state update lands is too late and produces silent
  // playback even though the element itself is technically playing. Resuming from a document-wide
  // click/touchstart listener guarantees the call happens inside the gesture itself.
  useEffect(() => {
    const unlock = () => {
      const ctx = audioGraphRef.current?.ctx;
      if (ctx && ctx.state === 'suspended') void ctx.resume();
    };
    document.addEventListener('click', unlock);
    document.addEventListener('touchstart', unlock);
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  // Keep voiceover/music playback state, position and volume in sync with the master clock.
  useEffect(() => {
    const graph = audioGraphRef.current;
    if (graph?.ctx.state === 'suspended' && playback.isPlaying) void graph.ctx.resume();

    syncVoiceover(voiceoverAudioRef.current, graph?.voiceGain ?? null, effectiveProject, playback);
    syncMusic(musicAudioRef.current, graph?.musicGain ?? null, effectiveProject, playback, computeTotalDuration(effectiveProject));
  });

  function handlePointerDown(target: 'caption' | 'watermark') {
    return (e: React.PointerEvent) => {
      e.stopPropagation();
      setDragTarget(target);
    };
  }

  useEffect(() => {
    if (!dragTarget) return;
    function onMove(e: PointerEvent) {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const xPercent = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      const yPercent = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
      // dispatched via CustomEvent so parent components (settings panels) needn't be re-rendered here
      window.dispatchEvent(new CustomEvent('reels:drag-position', { detail: { target: dragTarget, xPercent, yPercent, projectId: project.id } }));
    }
    function onUp() {
      setDragTarget(null);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragTarget, project.id]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        ref={containerRef}
        className="relative overflow-hidden rounded-2xl border-4 border-slate-800 bg-black shadow-lg"
        style={{ width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT }}
      >
        <canvas ref={canvasRef} width={PREVIEW_WIDTH} height={PREVIEW_HEIGHT} className="h-full w-full" />

        {effectiveProject.captions.position === 'custom' && (
          <div
            onPointerDown={handlePointerDown('caption')}
            className="absolute cursor-move border-2 border-dashed border-amber-400/70"
            style={{
              left: `${effectiveProject.captions.customRect.x * 100}%`,
              top: `${effectiveProject.captions.customRect.y * 100}%`,
              width: `${effectiveProject.captions.customRect.width * 100}%`,
              height: `${effectiveProject.captions.customRect.height * 100}%`,
            }}
            title="Drag to reposition captions"
          />
        )}
        {effectiveProject.watermark.enabled && effectiveProject.watermark.positionPreset === 'custom' && watermarkBitmap && (() => {
          const wmWidth = (effectiveProject.watermark.widthPercent / 100) * PREVIEW_WIDTH;
          const wmHeight = wmWidth * (watermarkBitmap.height / watermarkBitmap.width);
          const pos = resolveWatermarkPosition(
            'custom',
            null,
            wmWidth,
            wmHeight,
            PREVIEW_WIDTH,
            PREVIEW_HEIGHT,
            effectiveProject.watermark.offsetXPercent,
            effectiveProject.watermark.offsetYPercent
          );
          return (
            <div
              onPointerDown={handlePointerDown('watermark')}
              className="absolute cursor-move rounded border-2 border-dashed border-brand-400/80"
              style={{ left: pos.x, top: pos.y, width: wmWidth, height: wmHeight }}
              title="Drag to reposition watermark"
            />
          );
        })()}

        {effectiveProject.voiceover.timingSource === 'estimated' && effectiveProject.captions.enabled && (
          <span className="absolute right-2 top-2 rounded bg-amber-500/90 px-1.5 py-0.5 text-[10px] font-medium text-white">Estimated timing</span>
        )}
      </div>

      <audio ref={voiceoverAudioRef} src={voiceoverUrl ?? undefined} preload="auto" crossOrigin="anonymous" className="hidden" />
      <audio ref={musicAudioRef} src={musicUrl ?? undefined} preload="auto" crossOrigin="anonymous" loop={false} className="hidden" />
    </div>
  );
}

function syncVoiceover(
  audio: HTMLAudioElement | null,
  gain: GainNode | null,
  project: ReelProject,
  playback: PreviewPlayback
): void {
  if (!audio) return;
  const v = project.voiceover;
  if (!v.generatedMediaId) return;

  const audibleStart = v.startAt + v.silenceBefore;
  const naturalEnd = audibleStart + Math.max(0, (v.generatedDurationSeconds ?? 0) - v.trimStart - v.trimEnd);
  const targetEnd = v.endAt ?? naturalEnd;
  const inWindow = playback.currentTime >= audibleStart && playback.currentTime < targetEnd;

  if (gain) gain.gain.value = Math.min(4, Math.max(0, v.volume));

  if (!inWindow) {
    if (!audio.paused) audio.pause();
    return;
  }

  const targetAudioTime = playback.currentTime - audibleStart + v.trimStart;
  if (Math.abs(audio.currentTime - targetAudioTime) > 0.25) {
    audio.currentTime = Math.max(0, targetAudioTime);
  }
  if (playback.isPlaying && audio.paused) void audio.play().catch(() => undefined);
  if (!playback.isPlaying && !audio.paused) audio.pause();
}

function syncMusic(
  audio: HTMLAudioElement | null,
  gain: GainNode | null,
  project: ReelProject,
  playback: PreviewPlayback,
  totalDurationSeconds: number
): void {
  if (!audio) return;
  const m = project.music;
  if (!m.enabled || !m.mediaId || !Number.isFinite(audio.duration) || audio.duration === 0) return;

  const trimStart = m.trimStart;
  const trimEnd = m.trimEnd ?? audio.duration;
  const clipDuration = Math.max(0.1, trimEnd - trimStart);
  const relative = playback.currentTime - m.startAt;

  if (relative < 0) {
    if (!audio.paused) audio.pause();
    return;
  }
  if (!m.loop && relative > clipDuration) {
    if (!audio.paused) audio.pause();
    return;
  }

  const withinClip = m.loop ? relative % clipDuration : Math.min(relative, clipDuration);
  const targetAudioTime = trimStart + withinClip;
  if (Math.abs(audio.currentTime - targetAudioTime) > 0.3) {
    audio.currentTime = targetAudioTime;
  }
  if (playback.isPlaying && audio.paused) void audio.play().catch(() => undefined);
  if (!playback.isPlaying && !audio.paused) audio.pause();

  const voiceoverWindow = computeVoiceoverWindow(project.voiceover);
  const volume = computeMusicVolumeAtTime(playback.currentTime, m, totalDurationSeconds, voiceoverWindow);
  if (gain) gain.gain.value = Math.min(4, volume);
}
